import { Alert, Button, Icons } from '@commenda/kakashi';

import {
  IndirectTaxFilingsStatusBannerCopy,
  IndirectTaxFilingsStatusHelper,
  IndirectTaxFilingsStatusVerdict,
} from '../helpers';

/**
 * Optional 3rd action slot for the banner. Rendered as a button row beneath
 * the Alert (the kakashi Alert only exposes two action slots natively).
 * Currently only the US/CA breakdown tab uses this — to expose
 * "Sync aggregates" alongside the standard re-check + re-run pair.
 */
interface IndirectTaxFilingsStatusBannerTertiaryAction {
  label: string;
  onClick: () => void;
  isInProgress: boolean;
}

interface IndirectTaxFilingsStatusBannerProps {
  verdict: IndirectTaxFilingsStatusVerdict;
  filingCount: number;
  failedCount: number;
  rerunActionLabel: string;
  isRerunInProgress: boolean;
  onRecheckStatus: () => void;
  onRerunJob: () => void;
  tertiaryAction?: IndirectTaxFilingsStatusBannerTertiaryAction;
}

/**
 * Sticky banner that lives at the top of each tab body and gives the
 * operator the same affordances the legacy sales-tax modal had: a
 * verdict-tinted alert plus two actions — "Re-check status" (refetch the
 * status response) and "Re-run job" (re-fire breakdown generation /
 * contribution sync against the in-scope filing IDs).
 *
 * Styling and copy match the legacy modal so users don't have to relearn
 * the pattern, but the buttons drive filing_ids[] re-runs instead of the
 * legacy date-range path.
 */
const IndirectTaxFilingsStatusBanner = ({
  verdict,
  filingCount,
  failedCount,
  rerunActionLabel,
  isRerunInProgress,
  onRecheckStatus,
  onRerunJob,
  tertiaryAction,
}: IndirectTaxFilingsStatusBannerProps) => {
  const copy = IndirectTaxFilingsStatusHelper.getBannerCopy(
    verdict,
    filingCount,
    failedCount
  );

  return (
    <div className="flex flex-col gap-md">
      <Alert
        variant={copy.variant}
        title={copy.title}
        description={copy.description}
        icon={renderIcon(copy.variant)}
        action={{
          label: 'Re-check status',
          onClick: onRecheckStatus,
          variant: 'secondary',
        }}
        secondaryAction={{
          // No `disabled` field on the Alert's secondaryAction shape — use a
          // no-op while a trigger is in flight so the user can't double-fire,
          // and append "…" to the label so the disabled state is visible.
          label: isRerunInProgress ? `${rerunActionLabel}…` : rerunActionLabel,
          onClick: isRerunInProgress ? () => undefined : onRerunJob,
          variant: 'tertiary',
        }}
      />
      {tertiaryAction && (
        <div className="flex justify-end">
          <Button
            variant="secondary"
            size="sm"
            disabled={tertiaryAction.isInProgress}
            onClick={tertiaryAction.onClick}
          >
            {tertiaryAction.isInProgress
              ? `${tertiaryAction.label}…`
              : tertiaryAction.label}
          </Button>
        </div>
      )}
    </div>
  );
};

function renderIcon(
  variant: IndirectTaxFilingsStatusBannerCopy['variant']
): JSX.Element {
  switch (variant) {
    case 'success':
      return <Icons.CheckCircle className="size-5 text-text-success" />;
    case 'destructive':
      return <Icons.AlertCircle className="size-5 text-text-error" />;
    case 'warning':
    default:
      return <Icons.AlertCircle className="size-5 text-text-warning" />;
  }
}

export { IndirectTaxFilingsStatusBanner };
